import { FC } from 'react';

import {
  Button,
  Flex,
  IconButton,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import { MdAddCircleOutline, MdClose } from 'react-icons/md';

import {
  FEE_ADJUSTMENT_TYPES,
  FeeAdjustmentRow,
  MAX_FEE_ADJUSTMENTS,
} from 'constants/feeAdjustments';

type FeeAdjustmentsEditorProps = {
  rows: FeeAdjustmentRow[];
  onChange: (rows: FeeAdjustmentRow[]) => void;
};

/**
 * Editable list of typed fee / adjustment rows (type pulldown + amount) used on
 * the order and harvest-report invoicing tabs. Row state is owned by the parent.
 */
const FeeAdjustmentsEditor: FC<FeeAdjustmentsEditorProps> = ({
  rows,
  onChange,
}) => {
  const updateRow = (index: number, patch: Partial<FeeAdjustmentRow>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  return (
    <Flex direction="column" gap="10px">
      {rows.map((row, index) => (
        <Flex key={index} gap="8px" align="center">
          <Select
            size="sm"
            flex={1.5}
            value={row.type}
            onChange={(e) => updateRow(index, { type: e.target.value })}>
            {FEE_ADJUSTMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
          <Text fontWeight="bold">+</Text>
          <Input
            size="sm"
            flex={1}
            maxW="100px"
            type="number"
            value={row.amount === 0 ? '' : row.amount}
            placeholder="0"
            onWheel={(e: any) => e.target?.blur?.()}
            onChange={(e) =>
              updateRow(index, {
                amount: parseFloat(e.target.value) || 0,
              })
            }
          />
          <IconButton
            aria-label="Remove fee/adjustment"
            icon={<MdClose />}
            size="sm"
            variant="ghost"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
        </Flex>
      ))}
      {rows.length < MAX_FEE_ADJUSTMENTS && (
        <Button
          size="sm"
          variant="ghost"
          alignSelf="flex-start"
          leftIcon={<MdAddCircleOutline size="18px" />}
          onClick={() =>
            // New rows default to the first type in the pulldown (Deposit).
            onChange([...rows, { type: FEE_ADJUSTMENT_TYPES[0], amount: 0 }])
          }>
          Add Fee/Adjustment
        </Button>
      )}
    </Flex>
  );
};

export default FeeAdjustmentsEditor;
